import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet";

const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const defaultPreferences = ["Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const defaultTeams = [{ "name": "Royals", "group": "A", "preferences": ["Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] }];
const defaultMatchDays = { Mon: 2, Tue: 2, Wed: 2, Thu: 2, Fri: 2, Sat: 8, Sun: 8 };
const defaultMatchTimes = {
  Mon: ["6:00 PM"],
  Tue: ["6:00 PM"],
  Wed: ["6:00 PM"],
  Thu: ["6:00 PM"],
  Fri: ["6:00 PM"],
  Sat: ["8:00 AM", "11:00 AM", "3:00 PM", "6:00 PM"],
  Sun: ["8:00 AM", "11:00 AM", "3:00 PM", "6:00 PM"]
};

// Helper to get next coming Saturday ..
const getNextSaturday = () => {
  let date = new Date();
  const day = date.getDay();
  // 6 = Saturday
  const daysUntilSaturday = (6 - day + 7) % 7 || 7;
  date.setDate(date.getDate() + daysUntilSaturday);
  return date.toISOString().split('T')[0];
};

const defaultStartDate = getNextSaturday();
const defaultNumGrounds = 2;

const MatchScheduler = ({ hideHelpFloater = false }) => {
  const [teams, setTeams] = useState(defaultTeams);
  const [matchDays, setMatchDays] = useState(defaultMatchDays);
  const [matchTimes, setMatchTimes] = useState(defaultMatchTimes);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [numGrounds, setNumGrounds] = useState(defaultNumGrounds);
  const [schedule, setSchedule] = useState([]);
  const [maxGamesPerWeekSummary, setMaxGamesPerWeekSummary] = useState([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [umpireCounts, setUmpireCounts] = useState({});
  const [assignUmpires, setAssignUmpires] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showFloatingButtons, setShowFloatingButtons] = useState(true);
  const outputRef = useRef(null);

  // PayPal donation URL - replace with your actual PayPal button URL
  const paypalDonateUrl = "https://www.paypal.com/donate/?hosted_button_id=FGPNV7YTRPPVE";
  const handleDonateFloater = () => {
    if (window.gtag) {
      window.gtag('event', 'donate_click', {
        event_category: 'engagement',
        event_label: 'Donate Floater Button'
      });
    }
    window.open(paypalDonateUrl, '_blank');
  };

  const handleDonate = () => {
    if (window.gtag) {
      window.gtag('event', 'donate_click', {
        event_category: 'engagement',
        event_label: 'Donate Button'
      });
    }
    window.open(paypalDonateUrl, '_blank');
  };

  const handleTeamChange = (index, field, value) => {
    const updatedTeams = [...teams];
    updatedTeams[index][field] = value;
    setTeams(updatedTeams);
  };

  const handleTeamPreferencesChange = (index, day) => {
    const updatedTeams = [...teams];
    const preferences = updatedTeams[index].preferences || [];
    if (preferences.includes(day)) {
      updatedTeams[index].preferences = preferences.filter(d => d !== day);
    } else {
      updatedTeams[index].preferences = [...preferences, day];
    }
    setTeams(updatedTeams);
  };

  const handleMatchDayChange = (day, value) => {
    setMatchDays({ ...matchDays, [day]: Number(value) });
  };

  const handleMatchTimeChange = (day, slots) => {
    setMatchTimes({ ...matchTimes, [day]: slots.split(",").map(s => s.trim()) });
  };

  const handleAddTeam = () => {
    setTeams([...teams, { name: "", group: "", preferences: defaultPreferences.slice() }]);
  };

  const handleRemoveTeam = (index) => {
    if (teams.length > 1) {
      const updatedTeams = teams.filter((_, i) => i !== index);
      setTeams(updatedTeams);
    }
  };

  const handleSubmit = async () => {
    if (window.gtag) {
      window.gtag('event', 'generate_schedule_click', {
        event_category: 'engagement',
        event_label: 'Generate Schedule Button'
      });
    }
    setError("");
    setSchedule([]);
    setMaxGamesPerWeekSummary([]);
    setLoading(true);
    
    // Basic frontend validation
    if (!startDate) {
      setError("Please select a start date");
      setLoading(false);
      return;
    }
    
    if (teams.some(team => !team.name || !team.group)) {
      setError("Please fill in all team names and groups");
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch("https://match-scheduler-backend.onrender.com/api/generate-schedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          teams,
          matchDays,
          matchTimes,
          startDate,
          numGrounds,
          assignUmpires,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (window.gtag) {
          window.gtag('event', 'schedule_generation_error', {
            event_category: 'error',
            event_label: data.error || 'Unknown error'
          });
        }
        setError(data.error || "Failed to generate schedule");
        setLoading(false);
        return;
      }
      
      // Support both old and new backend response
      if (Array.isArray(data.schedule)) {
        setSchedule(data.schedule);
        setMaxGamesPerWeekSummary(data.maxGamesPerWeekSummary || []);
        setUmpireCounts(data.umpireCounts || {});
      } else {
        setSchedule(data.schedule || []);
        setMaxGamesPerWeekSummary(data.maxGamesPerWeekSummary || []);
        setUmpireCounts(data.umpireCounts || {});
      }
      
      if (data.schedule && data.schedule.length === 0) {
        setError("No schedule could be generated with the current constraints. Please adjust your inputs.");
      }
      setTimeout(() => {
        if (outputRef.current) {
          outputRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (e) {
      if (window.gtag) {
        window.gtag('event', 'schedule_generation_error', {
          event_category: 'error',
          event_label: e.message || 'Unknown error (exception)'
        });
      }
      setError("Failed to generate schedule. Please check if the backend is running.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  // Function to copy schedule table as TSV for Excel
  const handleCopySchedule = () => {
    if (window.gtag) {
      window.gtag('event', 'copy_schedule_click', {
        event_category: 'engagement',
        event_label: 'Copy Schedule Table'
      });
    }
    if (!schedule.length) return;
    // Dynamically set headers based on umpiring option
    const headers = assignUmpires
      ? ["Date", "Day", "Time", "Match", "Ground", "Group", "Umpiring Team"]
      : ["Date", "Day", "Time", "Match", "Ground", "Group"];
    // Sort schedule by Date and then by Time (ascending)
    const sortedSchedule = [...schedule].sort((a, b) => {
      // Parse date as yyyy-mm-dd for sorting
      const parseDate = (d) => {
        // Accept both yyyy-mm-dd and mm/dd/yyyy
        if (d.includes("-")) {
          const [y, m, day] = d.split("-");
          return new Date(Date.UTC(y, m - 1, day));
        } else {
          const [m, day, y] = d.split("/");
          return new Date(Date.UTC(y, m - 1, day));
        }
      };
      const dateA = parseDate(a["Date"]);
      const dateB = parseDate(b["Date"]);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      // Compare time as 24-hour
      const parseTime = t => {
        const [time, meridian] = t.split(" ");
        let [h, min] = time.split(":").map(Number);
        if (meridian && meridian.toLowerCase() === "pm" && h !== 12) h += 12;
        if (meridian && meridian.toLowerCase() === "am" && h === 12) h = 0;
        return h * 60 + min;
      };
      return parseTime(a["Time"]) - parseTime(b["Time"]);
    });
    const rows = sortedSchedule.map(match => {
      const team1 = match["Match"].split(" Vs ")[0];
      const teamObj = teams.find(t => t.name === team1);
      const group = teamObj ? teamObj.group : "";
      const [year, month, day] = match["Date"].split('-');
      const dateObj = new Date(Date.UTC(year, month - 1, day));
      const dayAbbr = dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
      // Format date as mm/dd/yyyy
      const formattedDate = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
      const baseRow = [
        formattedDate,
        dayAbbr,
        match["Time"],
        match["Match"],
        match["Ground"],
        group
      ];
      if (assignUmpires) {
        return [...baseRow, match["Umpires"] || ""];
      }
      return baseRow;
    });
    const tsv = [headers.join("\t"), ...rows.map(row => row.join("\t"))].join("\n");
    navigator.clipboard.writeText(tsv).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const GOOGLE_FORM_ACTION_URL = 'https://docs.google.com/forms/u/0/d/e/1FAIpQLScT2_qMhmfxINfvup2ey-U7A4Y0yygZTkLcvf7s_C7Cic933g/formResponse';
  const RATING_FIELD = 'entry.2125740814';
  const COMMENT_FIELD = 'entry.1473768020';
  const EMAIL_FIELD = 'entry.1592077490'; // If you want to collect email

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    if (window.gtag) {
      window.gtag('event', 'feedback_submit', {
        event_category: 'engagement',
        event_label: 'Feedback Submitted'
      });
    }
    const formData = new FormData();
    formData.append(RATING_FIELD, feedbackRating);
    if (feedbackText.trim()) formData.append(COMMENT_FIELD, feedbackText.trim());
    if (userEmail.trim()) formData.append(EMAIL_FIELD, userEmail.trim());

    fetch(GOOGLE_FORM_ACTION_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: formData,
    });

    setFeedbackSubmitted(true);
    setTimeout(() => {
      setShowFeedbackModal(false);
      setFeedbackSubmitted(false);
      setFeedbackRating(0);
      setFeedbackText('');
      setUserEmail('');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
      <Helmet>
        <title>Sports Match Scheduler | Free Online League & Tournament Tool</title>
        <meta name="description" content="Create fair, balanced round-robin schedules for your sports league or tournament in seconds. Free online match scheduler with Excel export, umpire assignment, and mobile-friendly design. No sign-up required!" />
        <link rel="canonical" href="https://truth-ai-tech.com/match-scheduler" />
      </Helmet>
      <div className="max-w-5xl mx-auto py-10 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2 text-center">Sports Match Scheduler</h1>
        <p className="text-lg text-blue-800 mb-6 text-center">Create fair, balanced round-robin schedules for your league or tournament. Export to Excel, assign umpires, and more – all for free!</p>
        {/* Donation Section */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Truth Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span 
                className="text-2xl font-bold text-white hidden"
                style={{ display: 'none' }}
              >
                Truth
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <h3 className="text-xl font-bold text-white mb-2">☕ Support Truth</h3>
              <p className="text-yellow-100 text-sm">If this tool helps you, consider buying me a coffee!</p>
            </div>
            <button
              onClick={handleDonate}
              className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition shadow-md flex items-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.067 8.478c.492.315.844.825.844 1.522 0 .697-.352 1.207-.844 1.522-.492.315-1.156.478-1.844.478H5.777c-.688 0-1.352-.163-1.844-.478C3.441 11.207 3.089 10.697 3.089 10c0-.697.352-1.207.844-1.522.492-.315 1.156-.478 1.844-.478h12.446c.688 0 1.352.163 1.844.478z"/>
              </svg>
              Donate with PayPal
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-10 border border-blue-100">
          <h2 className="text-xl font-semibold text-blue-700 mb-4">Teams</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {teams.map((team, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-4 border border-blue-100 flex flex-col gap-2 shadow-sm relative">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Team Name"
                    value={team.name}
                    onChange={(e) => handleTeamChange(index, "name", e.target.value)}
                    className="border border-blue-200 rounded px-3 py-2 flex-1 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                  />
                  <input
                    type="text"
                    placeholder="Group (e.g. A)"
                    value={team.group}
                    onChange={(e) => handleTeamChange(index, "group", e.target.value)}
                    className="border border-blue-200 rounded px-3 py-2 w-24 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                  />
                  {teams.length > 1 && (
                    <button
                      onClick={() => handleRemoveTeam(index)}
                      className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 transition text-sm"
                      title="Remove team"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div>
                  <label className="block font-medium text-blue-700 mb-1">Preferences (Days):</label>
                  <div className="flex gap-2 flex-wrap">
                    {daysOfWeek.map((day) => (
                      <label key={day} className="flex items-center gap-1 text-blue-600">
                        <input
                          type="checkbox"
                          checked={team.preferences?.includes(day)}
                          onChange={() => handleTeamPreferencesChange(index, day)}
                          className="accent-blue-600"
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={handleAddTeam} className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 transition">+ Add Team</button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-10 border border-blue-100 flex flex-col md:flex-row gap-8">
          <div className="flex-1">
            <label className="block font-semibold text-blue-700 mb-2">Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-blue-200 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
            />
          </div>
          <div className="flex-1">
            <label className="block font-semibold text-blue-700 mb-2">Number of Grounds:</label>
            <input
              type="number"
              min={1}
              value={numGrounds}
              onChange={(e) => setNumGrounds(Number(e.target.value))}
              className="border border-blue-200 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-10 border border-blue-100 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-semibold text-blue-700 mb-2">Play Days & Matches per Day</h2>
            <div className="space-y-2">
              {daysOfWeek.map((day) => (
                <div key={day} className="flex items-center gap-2">
                  <label className="w-10 text-blue-600 font-medium">{day}:</label>
                  <input
                    type="number"
                    min={0}
                    value={matchDays[day]}
                    onChange={(e) => handleMatchDayChange(day, e.target.value)}
                    className="border border-blue-200 rounded px-2 py-1 w-20 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                  />
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-semibold text-blue-700 mb-2">Time Slots</h2>
            <div className="space-y-2">
              {daysOfWeek.map((day) => (
                <div key={day} className="flex items-center gap-2">
                  <label className="w-10 text-blue-600 font-medium">{day}:</label>
                  <input
                    type="text"
                    value={matchTimes[day]?.join(", ") || ""}
                    onChange={(e) => handleMatchTimeChange(day, e.target.value)}
                    className="border border-blue-200 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                    placeholder="e.g. 6:00 PM, 8:00 PM"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="assignUmpires"
            checked={assignUmpires}
            onChange={e => setAssignUmpires(e.target.checked)}
            className="mr-2 accent-blue-600"
          />
          <label htmlFor="assignUmpires" className="text-blue-800 font-medium">Include Umpiring Assignments</label>
        </div>

        <button
          onClick={handleSubmit}
          className="w-full md:w-auto block mx-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white px-10 py-3 rounded-xl text-lg font-semibold shadow-lg hover:from-blue-700 hover:to-blue-600 transition mb-8 flex items-center justify-center gap-2"
          disabled={loading}
        >
          {loading && (
            <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
          )}
          {loading ? "Generating..." : "Generate Schedule"}
        </button>

        {error && <div className="mt-4 text-red-600 text-center font-semibold bg-red-50 border border-red-200 rounded-lg py-3 px-4">{error}</div>}

        {schedule.length > 0 && (
          <div className="mt-12" ref={outputRef}>
            {/* Sticky Navigation Bar */}
            <nav className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-blue-100 mb-6 px-2 py-3 rounded-xl shadow flex flex-wrap gap-4 justify-center items-center" style={{scrollMarginTop: '80px'}}>
              <a href="#schedule-table" className="text-blue-700 font-semibold hover:underline focus:underline transition">Schedule</a>
              <a href="#max-games-summary" className="text-blue-700 font-semibold hover:underline focus:underline transition">Max Games Per Week</a>
              <a href="#home-team-summary" className="text-blue-700 font-semibold hover:underline focus:underline transition">{assignUmpires ? 'Home Team and Umpiring Summary' : 'Home Team Summary'}</a>
              <a href="#idle-gap-summary" className="text-blue-700 font-semibold hover:underline focus:underline transition">Idle Gap Summary</a>
            </nav>

            {/* Schedule Table */}
            <div id="schedule-table">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
                <h2 className="font-bold text-2xl text-blue-800">Generated Schedule</h2>
                <button
                  onClick={handleCopySchedule}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 transition"
                >
                  {copied ? "Copied!" : "Copy Schedule Table for Excel"}
                </button>
              </div>
              <div className="overflow-x-auto rounded-xl shadow border border-blue-100 bg-white">
                <table className="table-auto border-collapse w-full">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="border px-4 py-2">Date</th>
                      <th className="border px-4 py-2">Day</th>
                      <th className="border px-4 py-2">Time</th>
                      <th className="border px-4 py-2">Match</th>
                      <th className="border px-4 py-2">Ground</th>
                      <th className="border px-4 py-2">Group</th>
                      {assignUmpires && <th className="border px-4 py-2">Umpiring Team</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {schedule
                      .slice() // copy to avoid mutating state
                      .sort((a, b) => {
                        // Parse date as yyyy-mm-dd for sorting
                        const parseDate = (d) => {
                          // Accept both yyyy-mm-dd and mm/dd/yyyy
                          if (d.includes("-")) {
                            const [y, m, day] = d.split("-");
                            return new Date(Date.UTC(y, m - 1, day));
                          } else {
                            const [m, day, y] = d.split("/");
                            return new Date(Date.UTC(y, m - 1, day));
                          }
                        };
                        const dateA = parseDate(a["Date"]);
                        const dateB = parseDate(b["Date"]);
                        if (dateA.getTime() !== dateB.getTime()) {
                          return dateA.getTime() - dateB.getTime();
                        }
                        // Compare time as 24-hour
                        const parseTime = t => {
                          const [time, meridian] = t.split(" ");
                          let [h, min] = time.split(":").map(Number);
                          if (meridian && meridian.toLowerCase() === "pm" && h !== 12) h += 12;
                          if (meridian && meridian.toLowerCase() === "am" && h === 12) h = 0;
                          return h * 60 + min;
                        };
                        return parseTime(a["Time"]) - parseTime(b["Time"]);
                      })
                      .map((match, idx) => {
                        // Extract the first team name from the Match string
                        const team1 = match["Match"].split(" Vs ")[0];
                        // Find the group for this team
                        const teamObj = teams.find(t => t.name === team1);
                        const group = teamObj ? teamObj.group : "";
                        // Get the day abbreviation from the date (parse as UTC to avoid timezone issues)
                        const [year, month, day] = match["Date"].split('-');
                        const dateObj = new Date(Date.UTC(year, month - 1, day));
                        const dayAbbr = dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
                        // Format date as mm/dd/yyyy
                        const formattedDate = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
                        return (
                          <tr key={idx}>
                            <td className="border px-4 py-2">{formattedDate}</td>
                            <td className="border px-4 py-2">{dayAbbr}</td>
                            <td className="border px-4 py-2">{match["Time"]}</td>
                            <td className="border px-4 py-2">{match["Match"]}</td>
                            <td className="border px-4 py-2">{match["Ground"]}</td>
                            <td className="border px-4 py-2">{group}</td>
                            {assignUmpires && <td className="border px-4 py-2">{match["Umpires"]}</td>}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Max Games Per Week Summary Table */}
            {maxGamesPerWeekSummary.length > 0 && (
              <div className="mt-8" id="max-games-summary">
                <h2 className="font-bold text-lg mb-4">Max Games Per Week Summary</h2>
                <table className="table-auto border-collapse w-full">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="border px-4 py-2">Team</th>
                      <th className="border px-4 py-2">Max Games In A Week</th>
                      <th className="border px-4 py-2">Dates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maxGamesPerWeekSummary.map((row, idx) => (
                      <tr key={idx}>
                        <td className="border px-4 py-2">{row.team}</td>
                        <td className="border px-4 py-2">{row.maxGamesInAWeek}</td>
                        <td className="border px-4 py-2">{
                          row.dates && row.dates.length > 0
                            ? row.dates.map(dateStr => {
                                // Accept both yyyy-mm-dd and mm/dd/yyyy
                                let dateObj;
                                if (dateStr.includes("-")) {
                                  const [y, m, d] = dateStr.split("-");
                                  dateObj = new Date(Date.UTC(y, m - 1, d));
                                } else {
                                  const [mm, dd, yy] = dateStr.split("/");
                                  dateObj = new Date(Date.UTC(yy, mm - 1, dd));
                                }
                                return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
                              }).join(', ')
                            : ''
                        }</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Home Team and Umpiring Summary Table */}
            {assignUmpires && (
              <div className="mt-8" id="home-team-summary">
                <h2 className="font-bold text-lg mb-4">Home Team and Umpiring Summary</h2>
                <table className="table-auto border-collapse w-full">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="border px-4 py-2">Group</th>
                      <th className="border px-4 py-2">Team</th>
                      <th className="border px-4 py-2">Home Matches</th>
                      {assignUmpires && <th className="border px-4 py-2">Umpiring Assignments</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {teams
                      .slice()
                      .sort((a, b) => {
                        if (a.group !== b.group) return a.group.localeCompare(b.group);
                        return a.name.localeCompare(b.name);
                      })
                      .map((team, idx) => {
                        const homeCount = schedule.filter(match => match["Home Team"] === team.name).length;
                        const umpireCount = umpireCounts[team.name] || 0;
                        return (
                          <tr key={idx}>
                            <td className="border px-4 py-2">{team.group}</td>
                            <td className="border px-4 py-2">{team.name}</td>
                            <td className="border px-4 py-2">{homeCount}</td>
                            {assignUmpires && <td className="border px-4 py-2">{umpireCount}</td>}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}

            {!assignUmpires && (
              <div className="mt-8" id="home-team-summary">
                <h2 className="font-bold text-lg mb-4">Home Team Summary</h2>
                <table className="table-auto border-collapse w-full">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="border px-4 py-2">Group</th>
                      <th className="border px-4 py-2">Team</th>
                      <th className="border px-4 py-2">Home Matches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams
                      .slice()
                      .sort((a, b) => {
                        if (a.group !== b.group) return a.group.localeCompare(b.group);
                        return a.name.localeCompare(b.name);
                      })
                      .map((team, idx) => {
                        const homeCount = schedule.filter(match => match["Home Team"] === team.name).length;
                        return (
                          <tr key={idx}>
                            <td className="border px-4 py-2">{team.group}</td>
                            <td className="border px-4 py-2">{team.name}</td>
                            <td className="border px-4 py-2">{homeCount}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Idle Gap Summary Table */}
            <div className="mt-8" id="idle-gap-summary">
              <h2 className="font-bold text-lg mb-4">Idle Gap Summary</h2>
              <table className="table-auto border-collapse w-full">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border px-4 py-2">Team</th>
                    <th className="border px-4 py-2">Max Idle Weeks</th>
                    <th className="border px-4 py-2">Gap Start Date</th>
                    <th className="border px-4 py-2">Gap End Date</th>
                    <th className="border px-4 py-2">Game Before Gap</th>
                    <th className="border px-4 py-2">Game After Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map((team, idx) => {
                    // Get all games for this team, sorted by date
                    const games = schedule
                      .filter(match => match["Match"].includes(team.name))
                      .map(match => ({
                        date: new Date(match["Date"]),
                        match: match["Match"]
                      }))
                      .sort((a, b) => a.date - b.date);
                    let maxGap = 0;
                    let gapStart = null;
                    let gapEnd = null;
                    let beforeGame = null;
                    let afterGame = null;
                    // Consider gap from tournament start to first game
                    const tournamentStart = schedule.length > 0 ? new Date(schedule[0]["Date"]) : null;
                    if (games.length > 0 && tournamentStart) {
                      let prevDate = tournamentStart;
                      let prevMatch = null;
                      for (let i = 0; i < games.length; i++) {
                        const gapDays = Math.round((games[i].date - prevDate) / (1000 * 60 * 60 * 24));
                        const gapWeeks = Math.floor(gapDays / 7);
                        if (gapWeeks > maxGap) {
                          maxGap = gapWeeks;
                          gapStart = prevDate;
                          gapEnd = games[i].date;
                          beforeGame = prevMatch;
                          afterGame = games[i].match;
                        }
                        prevDate = games[i].date;
                        prevMatch = games[i].match;
                      }
                    }
                    // Also consider gap from last game to end of tournament
                    if (games.length > 0 && schedule.length > 0) {
                      const lastGameDate = games[games.length - 1].date;
                      const lastMatch = games[games.length - 1].match;
                      const tournamentEnd = new Date(schedule[schedule.length - 1]["Date"]);
                      const gapDays = Math.round((tournamentEnd - lastGameDate) / (1000 * 60 * 60 * 24));
                      const gapWeeks = Math.floor(gapDays / 7);
                      if (gapWeeks > maxGap) {
                        maxGap = gapWeeks;
                        gapStart = lastGameDate;
                        gapEnd = tournamentEnd;
                        beforeGame = lastMatch;
                        afterGame = null;
                      }
                    }
                    return (
                      <tr key={idx}>
                        <td className="border px-4 py-2">{team.name}</td>
                        <td className="border px-4 py-2">{maxGap}</td>
                        <td className="border px-4 py-2">{gapStart ? (() => {
                          // Format as mm/dd/yyyy
                          const d = new Date(gapStart);
                          return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
                        })() : ""}</td>
                        <td className="border px-4 py-2">{gapEnd ? (() => {
                          // Format as mm/dd/yyyy
                          const d = new Date(gapEnd);
                          return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
                        })() : ""}</td>
                        <td className="border px-4 py-2">{beforeGame || ""}</td>
                        <td className="border px-4 py-2">{afterGame || ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Floating Action Buttons Toggle */}
        <button
          onClick={() => setShowFloatingButtons(!showFloatingButtons)}
          className="fixed bottom-6 right-6 z-50 bg-gray-600 text-white rounded-full shadow-lg p-3 hover:bg-gray-700 transition-all duration-300 flex items-center justify-center"
          aria-label={showFloatingButtons ? "Hide floating buttons" : "Show floating buttons"}
        >
          {showFloatingButtons ? (
            // Collapse icon (chevron down)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          ) : (
            // Expand icon (chevron up)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          )}
        </button>

        {/* Floating Help Button */}
        {showFloatingButtons && !hideHelpFloater && (
          <a
            href="/user-guide"
            className="fixed bottom-52 right-6 z-40 bg-green-600 text-white rounded-full shadow-lg px-6 py-3 font-semibold text-lg hover:bg-green-700 transition-all duration-300 flex items-center gap-2 animate-fade-in"
            style={{position: 'fixed', bottom: 208, right: 24, zIndex: 9999}}
            aria-label="Help & Guide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="hidden sm:inline">Help</span>
          </a>
        )}
        {/* Donate Floater Button */}
        {showFloatingButtons && (
          <button
            className="fixed bottom-36 right-6 z-40 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full shadow-lg px-6 py-3 font-semibold text-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 flex items-center gap-2 animate-fade-in"
            style={{position: 'fixed', bottom: 144, right: 24, zIndex: 9999}}
            onClick={handleDonateFloater}
            aria-label="Donate with PayPal"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            <span className="hidden sm:inline">Donate</span>
          </button>
        )}
        {/* Floating Feedback Button */}
        {showFloatingButtons && (
          <button
            className="fixed bottom-20 right-6 z-40 bg-blue-600 text-white rounded-full shadow-lg px-6 py-3 font-semibold text-lg hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 animate-fade-in"
            style={{position: 'fixed', bottom: 80, right: 24, zIndex: 9999}}
            onClick={() => setShowFeedbackModal(true)}
            aria-label="Give Feedback"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m-2 8a9 9 0 110-18 9 9 0 010 18z" /></svg>
            <span className="hidden sm:inline">Feedback</span>
          </button>
        )}

        {/* Feedback Modal */}
        {showFeedbackModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative animate-fade-in">
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl font-bold"
                onClick={() => setShowFeedbackModal(false)}
                aria-label="Close Feedback"
              >
                ×
              </button>
              {feedbackSubmitted ? (
                <div className="text-center py-8">
                  <div className="text-3xl mb-2">🎉</div>
                  <div className="text-lg font-semibold text-blue-700">Thank you for your feedback!</div>
                </div>
              ) : (
                <form onSubmit={handleFeedbackSubmit} className="flex flex-col gap-4">
                  <h2 className="text-xl font-bold text-blue-800 mb-2 text-center">We value your feedback</h2>
                  <div className="flex items-center justify-center gap-2">
                    {[1,2,3,4,5].map(star => (
                      <button
                        type="button"
                        key={star}
                        onClick={() => setFeedbackRating(star)}
                        className={star <= feedbackRating ? "text-yellow-400" : "text-gray-300"}
                        aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                      >
                        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" /></svg>
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="border border-blue-200 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition min-h-[80px]"
                    placeholder="Your comments (optional)"
                    value={feedbackText}
                    onChange={e => setFeedbackText(e.target.value)}
                    maxLength={500}
                  />
                  <input
                    type="email"
                    className="border border-blue-200 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                    placeholder="Your email (optional)"
                    value={userEmail}
                    onChange={e => setUserEmail(e.target.value)}
                    maxLength={100}
                  />
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition mt-2"
                    disabled={feedbackRating === 0}
                  >
                    Submit Feedback
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Loading Animation Overlay */}
        {loading && (
  <div style={styles.modalOverlay}>
    <div style={styles.modalContent}>
      <img src="/A.I-2.gif" alt="Loading..." style={styles.loader} />
      <p style={{ color: "#fff", marginTop: 10 }}>Generating your schedule...</p>
    </div>
  </div>
)}

        {/* SEO FAQ Section */}
        <section className="mt-16 bg-white rounded-xl shadow p-8 max-w-3xl mx-auto" id="faq">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">Frequently Asked Questions</h2>
          <div className="mb-4">
            <h3 className="font-semibold text-blue-700">How do I schedule matches online?</h3>
            <p>Simply enter your teams, match days, and other preferences, then click "Generate Schedule." The tool will create a fair, balanced round-robin schedule for your league or tournament.</p>
          </div>
          <div className="mb-4">
            <h3 className="font-semibold text-blue-700">Is this match scheduler free?</h3>
            <p>Yes! Our sports match scheduler is completely free to use for leagues, tournaments, and clubs of all sizes.</p>
          </div>
          <div className="mb-4">
            <h3 className="font-semibold text-blue-700">Can I export the schedule to Excel?</h3>
            <p>Yes, you can copy the generated schedule and paste it directly into Excel or Google Sheets for easy sharing and editing.</p>
          </div>
          <div className="mb-4">
            <h3 className="font-semibold text-blue-700">Does it support umpiring and home/away balancing?</h3>
            <p>Absolutely! The scheduler automatically assigns umpiring duties and balances home/away games for fairness.</p>
          </div>
          <div className="mb-4">
            <h3 className="font-semibold text-blue-700">Is it mobile-friendly?</h3>
            <p>Yes, the match scheduler is fully responsive and works great on all devices.</p>
          </div>
        </section>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
     position: "fixed",
     top: 0,
     left: 0,
     width: "100vw",
     height: "100vh",
     backgroundColor: "rgba(0, 0, 0, 0.5)",
     backdropFilter: "blur(6px)",
     display: "flex",
     alignItems: "center",
     justifyContent: "center",
     zIndex: 9999,
   },
   
   modalContent: {
     backgroundColor: "rgba(20, 20, 60, 0.85)",
     padding: 40,
     borderRadius: 10,
     textAlign: "center",
     boxShadow: "0 0 20px rgba(0,0,0,0.2)",
   },
   
   loader: {
     width: 500,
     height: 300,
   }
 };

export default MatchScheduler;